import { observer, inject } from "mobx-react";
import { IconButton, Input, Select, Status } from "../UI";
import styled from "styled-components";
import DeleteImg from "../asserts/img/delete_doc.svg";
import EditImg from "../asserts/img/edit_doc.svg";
import CancelImg from "../asserts/img/cancel.svg";
import UpdateImg from "../asserts/img/done.svg";
import { useTranslation } from "react-i18next";
import { openAttachment } from "../utils/attachmentViewer";
import { useEffect, useState } from "react";
import { renderKeyValue } from "../components/Common";

const StyledBox = styled.div(({ theme, backgroungColor }) => ({
  borderBottom: "1px solid #EAE9F1",
  borderRadius: 0,
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  flexWrap: "wrap",
  background: backgroungColor ? backgroungColor : theme.color.white,
  boxShadow: `0px 4px 30px rgba(74, 105, 166, 0.13)`,
  cursor: "pointer",
  height: 48,

  "&[alt='edit row']": {},

  // Row actions stay visible (for touch and keyboard users), dimmed until the row is hovered or focused
  ".row-action": {
    opacity: 0.45,
    transition: "opacity 0.15s",
  },
  "&:hover .row-action, &:focus-within .row-action": {
    opacity: 1,
  },
}));

// Keeps the icons' footprint small enough for the table's actions column
const ROW_ACTION_STYLE = { margin: "-7px 0", minWidth: 32, minHeight: 32 };

const BoxContent = styled.div(({ width }) => ({
  width: width,
  paddingInlineStart: 10,
  paddingInlineEnd: 10,
  borderRadius: 7,
}));

const Value = styled.label(({ theme, valueColor }) => ({
  fontSize: 14,
  color: valueColor ? valueColor : theme.color.text,
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
}));

const IndexValue = styled.label(({ theme }) => ({
  fontSize: 16,
  fontWeight: 900,
  color: theme.color.lightText,
}));

const TableRow = ({
  keys,
  row,
  index,
  hasDelete,
  handelRowDeleteClick = () => {},
  handelRowUpdateClick = () => {},
  handelRowClick = () => {},
  handelRowCancel = () => {},
  withIndex,
  translation,
  backgroungColor,
  valueColor,
  editable,
  onClick,
}) => {
  const { t } = useTranslation(translation);
  const [selectedRow, setSelectedRow] = useState(row);

  useEffect(() => {
    setSelectedRow(row);
  }, [row]);

  const handleInputChange = (changedRow, key, e) => {
    setSelectedRow({ ...changedRow, [key.key]: e.target.value });
  };

  const handleSelectChange = (changedRow, key, value) => {
    setSelectedRow({ ...changedRow, [key.key]: value?.value });
  };

  const handleCancelClick = (e, selectedRow) => {
    e.stopPropagation();
    handelRowCancel(selectedRow);
    setSelectedRow({ ...row, isEditMode: false, isAddMode: false });
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    setSelectedRow({ ...row, isEditMode: true });
  };

  const renderCell = (key) => {
    const isEditable = selectedRow.isEditMode || selectedRow.isAddMode;

    if (key.key === "CODE" || key.key === "STATDES") {
      return (
        <Status
          row={selectedRow}
          status={selectedRow[key.key]}
          color={selectedRow.VCOLORNAME}
        />
      );
    }

    if (key.link) {
      return (
        <div
          style={{
            cursor: "pointer",
            color: "#007AEA",
            textDecoration: selectedRow[key.key] ? "underline" : "none",
          }}
          onClick={(e) => {
            e.stopPropagation();
            openAttachment(
              selectedRow["EXTFILENAME"],
              selectedRow[key.key],
              selectedRow["SUFFIX"]
            );
          }}
        >
          {selectedRow[key.key] || "-"}
        </div>
      );
    }

    if (key.input && isEditable) {
      return (
        <Input
          style={{ textAlign: "right" }}
          value={selectedRow[key.key]}
          onChange={(e) => handleInputChange(selectedRow, key, e)}
        />
      );
    }

    if (key.select && isEditable) {
      return (
        <Select
          height={29}
          width={200}
          value={
            selectedRow[key.key]
              ? { value: selectedRow[key.key], label: selectedRow[key.key] }
              : null
          }
          placeholder={t("common:choose")}
          options={key.selectData || []}
          onChange={(value) => handleSelectChange(selectedRow, key, value)}
        />
      );
    }

    return (
      <Value title={selectedRow[key.key]} valueColor={valueColor}>
        {renderKeyValue(key, selectedRow)}
      </Value>
    );
  };

  return (
    <StyledBox
      onClick={() => handelRowClick(selectedRow)}
      backgroungColor={backgroungColor}
    >
      {keys.map((key, i) => {
        if (withIndex && i === 0) {
          return (
            <BoxContent key={`index-${i}`} width={key.width || "3%"}>
              <IndexValue>
                {(index + 1).toLocaleString("en-US", {
                  minimumIntegerDigits: 2,
                  useGrouping: false,
                })}
              </IndexValue>
            </BoxContent>
          );
        }

        if (key.key === "ACTIONS") {
          return null;
        }

        return (
          <BoxContent
            style={{
              display: "flex",
              margin: key.key === "CODE" || (key.key === "STATDES" && 0),
              padding: key.key === "CODE" || (key.key === "STATDES" && 0),
            }}
            key={`${key.key}-${i}`}
            width={key.width}
          >
            {renderCell(key)}
          </BoxContent>
        );
      })}

      {selectedRow.isEditMode || selectedRow.isAddMode ? (
        <BoxContent>
          <button
            onClick={(e) => handelRowUpdateClick(e, selectedRow)}
            style={{
              border: 0,
              backgroundColor: "white",
              cursor: "pointer",
              verticalAlign: "middle",
            }}
          >
            <img alt="Update" src={UpdateImg} />
          </button>
          <button
            onClick={(e) => handleCancelClick(e, selectedRow)}
            style={{
              border: 0,
              backgroundColor: "white",
              cursor: "pointer",
              verticalAlign: "middle",
            }}
          >
            <img alt="Cancel" src={CancelImg} />
          </button>
        </BoxContent>
      ) : (
        (editable || hasDelete) && (
          <BoxContent>
            {editable && (
              <IconButton
                className="row-action"
                src={EditImg}
                iconSize={18}
                label={t("common:edit")}
                style={ROW_ACTION_STYLE}
                onClick={onClick || handleEditClick}
              />
            )}

            {hasDelete && (
              <IconButton
                className="row-action"
                src={DeleteImg}
                iconSize={18}
                label={t("common:delete")}
                style={ROW_ACTION_STYLE}
                onClick={(e) => {
                  e.preventDefault();
                  handelRowDeleteClick(e, selectedRow);
                }}
              />
            )}
          </BoxContent>
        )
      )}
    </StyledBox>
  );
};

export default inject()(observer(TableRow));
